import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

// Autoplaying video with a tap-to-unmute control (browsers block unmuted autoplay policies)
export function VideoWithSound({ src, poster, className = "", testId }) {
  const videoRef = useRef(null);
  const [on, setOn] = useState(false);
  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = on;
    if (!on) v.play().catch(() => {});
    setOn(!on);
  };
  return (
    <div className={`relative overflow-hidden rounded-lg ${className}`}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted={!on}
        loop
        playsInline
        data-testid={testId}
        className="h-full w-full object-cover"
      />
      <button
        data-testid={testId ? `${testId}-sound-toggle` : "video-sound-toggle"}
        onClick={toggle}
        className={`absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] backdrop-blur transition-colors ${
          on ? "bg-brand text-white" : "bg-navy/80 text-white hover:bg-brand"
        }`}
      >
        {on ? "Sound On" : "Sound Off"}
      </button>
    </div>
  );
}

export function Reveal({ children, delay = 0, y = 28, className = "", ...rest }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function MaskLines({
  lines,
  as: Tag = "h2",
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 0.1,
}) {
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <motion.span
          key={i}
          className="block overflow-hidden pb-[0.08em] -mb-[0.08em]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            variants={{ hidden: { y: "115%" }, visible: { y: "0%" } }}
            transition={{ duration: 0.9, delay: delay + i * stagger, ease: EASE }}
          >
            {line}
          </motion.span>
        </motion.span>
      ))}
    </Tag>
  );
}

export function ImageReveal({ src, alt = "", className = "", imgClassName = "", delay = 0, children, video = false, poster }) {
  return (
    <motion.div
      className={`relative overflow-hidden rounded-lg ${className}`}
      initial={{ clipPath: "inset(8% 4% 8% 4%)", opacity: 0 }}
      whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {video ? (
        <motion.video
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          className={`h-full w-full object-cover ${imgClassName}`}
          initial={{ scale: 1.12 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.4, delay, ease: EASE }}
        />
      ) : (
        <motion.img
          src={src}
          alt={alt}
          loading="lazy"
          className={`h-full w-full object-cover ${imgClassName}`}
          initial={{ scale: 1.14 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.4, delay, ease: EASE }}
        />
      )}
      {children}
    </motion.div>
  );
}

export function HoverZoomImage({ src, alt = "", className = "", imgClassName = "" }) {
  return (
    <div className={`group/hz relative overflow-hidden rounded-lg ${className}`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover/hz:scale-[1.05] ${imgClassName}`}
      />
    </div>
  );
}
